import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatDate, formatCurrency } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { CHART_COLORS, getGridColor, getTextColor, getTooltipStyle } from '../lib/chartColors';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { DollarSign, Download, TrendingUp, Wrench, Package, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CostAnalyticsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [assets, setAssets] = useState([]);

  // Filters
  const [fBranch, setFBranch] = useState('all');
  const [fRoom, setFRoom] = useState('all');
  const [fAsset, setFAsset] = useState('all');
  const [fType, setFType] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [granularity, setGranularity] = useState('monthly');

  // Data
  const [summary, setSummary] = useState(null);
  const [spendOverTime, setSpendOverTime] = useState([]);
  const [spendByBranch, setSpendByBranch] = useState([]);
  const [spendByRoom, setSpendByRoom] = useState([]);
  const [spendByAsset, setSpendByAsset] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/branches'), api.get('/rooms'), api.get('/assets'),
    ]).then(([b, r, a]) => {
      setBranches(b.data); setRooms(r.data); setAssets(a.data);
    }).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = {};
    if (fBranch !== 'all') p.branch_id = fBranch;
    if (fRoom !== 'all') p.room_id = fRoom;
    if (fAsset !== 'all') p.asset_id = fAsset;
    if (fType !== 'all') p.wo_type = fType;
    if (fStatus !== 'all') p.status = fStatus;
    if (fDateFrom) p.date_from = fDateFrom;
    if (fDateTo) p.date_to = fDateTo;
    return p;
  }, [fBranch, fRoom, fAsset, fType, fStatus, fDateFrom, fDateTo]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [sumRes, timeRes, brRes, roomRes, assetRes] = await Promise.all([
        api.get('/analytics/spend-summary', { params }),
        api.get('/analytics/spend-over-time', { params: { ...params, granularity } }),
        api.get('/analytics/spend-by-branch', { params }),
        api.get('/analytics/spend-by-room', { params }),
        api.get('/analytics/spend-by-asset', { params }),
      ]);
      setSummary(sumRes.data);
      setSpendOverTime(timeRes.data);
      setSpendByBranch(brRes.data);
      setSpendByRoom(roomRes.data);
      setSpendByAsset(assetRes.data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  }, [buildParams, granularity, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const clearFilters = () => {
    setFBranch('all'); setFRoom('all'); setFAsset('all');
    setFType('all'); setFStatus('all');
    setFDateFrom(''); setFDateTo('');
  };

  const handleExport = async () => {
    try {
      const params = buildParams();
      const res = await api.get('/analytics/export-csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'cost_analytics.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('toast.saved'));
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const filteredRooms = fBranch !== 'all' ? rooms.filter(r => r.branch_id === fBranch) : rooms;
  const filteredAssets = fRoom !== 'all' ? assets.filter(a => a.room_id === fRoom) : (fBranch !== 'all' ? assets.filter(a => a.branch_id === fBranch) : assets);

  return (
    <div data-testid="cost-analytics-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight">{t('analytics.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('analytics.spendOverTime')}</p>
        </div>
        <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-csv-btn">
          <Download className="w-3.5 h-3.5 me-1.5" /> {t('common.exportCSV')}
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.branch')}</Label>
              <Select value={fBranch} onValueChange={(v) => { setFBranch(v); setFRoom('all'); setFAsset('all'); }}>
                <SelectTrigger className="w-full sm:w-[160px] h-9" data-testid="ca-filter-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.room')}</Label>
              <Select value={fRoom} onValueChange={(v) => { setFRoom(v); setFAsset('all'); }}>
                <SelectTrigger className="w-full sm:w-[160px] h-9" data-testid="ca-filter-room"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')} {t('common.rooms')}</SelectItem>
                  {filteredRooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.asset')}</Label>
              <Select value={fAsset} onValueChange={setFAsset}>
                <SelectTrigger className="w-full sm:w-[160px] h-9" data-testid="ca-filter-asset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')} {t('common.assets')}</SelectItem>
                  {filteredAssets.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.type')}</Label>
              <Select value={fType} onValueChange={setFType}>
                <SelectTrigger className="w-full sm:w-[140px] h-9" data-testid="ca-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="corrective">{t('workOrder.corrective')}</SelectItem>
                  <SelectItem value="preventive">{t('workOrder.preventive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.status')}</Label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="w-full sm:w-[140px] h-9" data-testid="ca-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="COMPLETED">{t('status.COMPLETED')}</SelectItem>
                  <SelectItem value="CLOSED">{t('status.CLOSED')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('analytics.startDate')}</Label>
              <Input type="date" className="w-[140px] h-9" value={fDateFrom} onChange={(e) => setFDateFrom(e.target.value)} data-testid="ca-filter-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('analytics.endDate')}</Label>
              <Input type="date" className="w-[140px] h-9" value={fDateTo} onChange={(e) => setFDateTo(e.target.value)} data-testid="ca-filter-to" />
            </div>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9">
              {t('analytics.clearFilters')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('analytics.totalSpend')}</p>
                <p className="text-2xl font-bold text-brand font-barlow">{formatCurrency(summary.total)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <UsersIcon className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('analytics.laborCosts')}</p>
                <p className="text-2xl font-bold text-foreground font-barlow">{formatCurrency(summary.labor)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('analytics.partsCosts')}</p>
                <p className="text-2xl font-bold text-foreground font-barlow">{formatCurrency(summary.parts)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('analytics.vendorCosts')}</p>
                <p className="text-2xl font-bold text-foreground font-barlow">{formatCurrency(summary.vendor)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spend Over Time */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-barlow">{t('analytics.spendOverTime')}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={granularity === 'weekly' ? 'default' : 'outline'} onClick={() => setGranularity('weekly')}>
                {t('analytics.weeklyView')}
              </Button>
              <Button size="sm" variant={granularity === 'monthly' ? 'default' : 'outline'} onClick={() => setGranularity('monthly')}>
                {t('analytics.monthlyView')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {spendOverTime.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">{t('analytics.noData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={spendOverTime}>
                  <CartesianGrid strokeDasharray="3 3" stroke={getGridColor(isDark)} />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: getTextColor(isDark) }} />
                  <YAxis tick={{ fontSize: 11, fill: getTextColor(isDark) }} tickFormatter={(v) => `${v/1000}k`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} {...getTooltipStyle(isDark)} />
                  <Legend wrapperStyle={{ color: getTextColor(isDark) }} />
                  <Line type="monotone" dataKey="total" stroke={CHART_COLORS.primary} strokeWidth={2} name={t('analytics.totalSpend')} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Spend by Branch */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-barlow">{t('analytics.spendByBranch')}</CardTitle>
          </CardHeader>
          <CardContent>
            {spendByBranch.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">{t('analytics.noData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={spendByBranch} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={getGridColor(isDark)} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: getTextColor(isDark) }} tickFormatter={(v) => `${v/1000}k`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: getTextColor(isDark) }} width={100} />
                  <Tooltip formatter={(v) => formatCurrency(v)} {...getTooltipStyle(isDark)} />
                  <Bar dataKey="total" fill={CHART_COLORS.secondary} radius={[0, 4, 4, 0]} name={t('analytics.totalSpend')} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Spend by Room */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-barlow">{t('analytics.spendByRoom')}</CardTitle>
          </CardHeader>
          <CardContent>
            {spendByRoom.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">{t('analytics.noData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={spendByRoom.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={getGridColor(isDark)} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: getTextColor(isDark) }} tickFormatter={(v) => `${v/1000}k`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: getTextColor(isDark) }} width={100} />
                  <Tooltip formatter={(v) => formatCurrency(v)} {...getTooltipStyle(isDark)} />
                  <Bar dataKey="total" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} name={t('analytics.totalSpend')} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top Assets by Cost */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-barlow">{t('analytics.topAssets')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {spendByAsset.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">{t('analytics.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.asset')}</th>
                      <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('analytics.laborCosts')}</th>
                      <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('analytics.partsCosts')}</th>
                      <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('analytics.vendorCosts')}</th>
                      <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('analytics.totalSpend')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spendByAsset.slice(0, 10).map((a, i) => (
                      <tr key={a.asset_id || i} className="border-b border-zinc-50">
                        <td className="px-4 py-2.5 font-medium text-foreground">{a.name}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{formatCurrency(a.labor)}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{formatCurrency(a.parts)}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{formatCurrency(a.vendor)}</td>
                        <td className="px-4 py-2.5 font-semibold text-foreground">{formatCurrency(a.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
